// The eligibility-file-job-log status vocabulary and every partition of it the
// apps branch on. Mirrors tio-api's `EligibilityConstants`
// (app/services/eligibility_file_import/eligibility_constants.rb) — the two
// have to move together.

/**
 * Every status tio-api serves on a job log. `ABORTED_BY_ADMIN` has no tio-api
 * counterpart and is carried only so pre-existing rows still resolve.
 */
export const ELIGIBILITY_FILE_JOB_STATUSES = [
  'STARTED',
  'WAITING_FOR_UPLOAD',
  'VALIDATING_ELIGIBILITY_FILE',
  'REQUIRES_USER_APPROVAL',
  'REQUIRES_REVIEWER_APPROVAL',
  'EXECUTING_TASKS',
  'COMPLETED',
  'ABORTED_BY_USER',
  'ABORTED_BY_REVIEWER',
  'ABORTED_FOR_STALENESS',
  'ABORTED_AS_DUPLICATE',
  'ABORTED_BY_ADMIN',
  'ABORTED_UPLOAD_DISABLED',
  'ABORTED_PARTNER_DEACTIVATED',
  'ERROR_REPORTED',
  'FAILED_VALIDATION',
  'QUEUED',
  'QUEUED_OFFHOURS',
  'TEST_COMPLETED',
] as const;

export type EligibilityFileJobStatus = (typeof ELIGIBILITY_FILE_JOB_STATUSES)[number];

/** Statuses where work is actively moving, so a viewer's page is worth refreshing. */
export const POLLING_STATUSES = [
  'WAITING_FOR_UPLOAD',
  'QUEUED',
  'VALIDATING_ELIGIBILITY_FILE',
  'EXECUTING_TASKS',
] as const;

/**
 * Statuses reached by aborting. `ABORTED_UPLOAD_DISABLED` and
 * `ABORTED_PARTNER_DEACTIVATED` are written straight to the job log by the
 * eligibility-file preprocessor rather than by any abort action here.
 */
export const ABORTED_STATUSES = [
  'ABORTED_BY_USER',
  'ABORTED_BY_REVIEWER',
  'ABORTED_BY_ADMIN',
  'ABORTED_FOR_STALENESS',
  'ABORTED_AS_DUPLICATE',
  'ABORTED_UPLOAD_DISABLED',
  'ABORTED_PARTNER_DEACTIVATED',
] as const;

/** Statuses where the run finished and has real counts, rather than only preview counts. */
export const SUCCEEDED_STATUSES = ['COMPLETED', 'TEST_COMPLETED'] as const;

/**
 * Statuses a job never leaves, so polling stops. tio-api's `COMPLETED_STATES`
 * plus `TEST_COMPLETED`, which no action on this resource can move a job out
 * of — it is unabortable, unprocessable and not reprocessable.
 */
export const TERMINAL_STATUSES = [
  'COMPLETED',
  'ERROR_REPORTED',
  'TEST_COMPLETED',
  ...ABORTED_STATUSES,
] as const;

/** Statuses where the Abort action has nothing left to act on. */
export const UNABORTABLE_STATUSES = [...TERMINAL_STATUSES, 'FAILED_VALIDATION'] as const;

/** Statuses where the job is parked waiting on a human to approve or schedule it. */
export const AWAITING_APPROVAL_STATUSES = [
  'REQUIRES_USER_APPROVAL',
  'REQUIRES_REVIEWER_APPROVAL',
] as const;

/** Statuses a job can be pushed through a second time. */
export const CAN_REPROCESS_STATUSES = [...AWAITING_APPROVAL_STATUSES, 'FAILED_VALIDATION'] as const;
